import { AppGlueCoreInit } from "../../../../../AppGlueCoreInit";
import { DataDefinition } from "../../../../../rule-engine-core/Common/Data/DataDefinition";
import { BooleanDataDefinition } from "../../../../../rule-engine-core/Common/Data/Definitions/BooleanDataDefinition";
import { StringDataDefinition } from "../../../../../rule-engine-core/Common/Data/Definitions/StringDataDefinition";
import { ExecutionData } from "../../../../../rule-engine-core/Common/Data/ExecutionData/ExecutionData";
import { ExpressionRule } from "../../../../../rule-engine-core/Expressions/ExpressionRule";
import {
    IfThenExpression,
    IfThenPair,
    IfThenStyle,
} from "../../../../../rule-engine-core/Expressions/Expressions/Logic/IfThenExpression";
import { IsTrueExpression } from "../../../../../rule-engine-core/Expressions/Expressions/Logic/IsTrue";
import { StringEqualExpression } from "../../../../../rule-engine-core/Expressions/Expressions/String/StringEqualExpression";
import { StatementRuleConfiguration } from "../../../../../rule-engine-core/Rule/Statement/StatementRuleConfiguration";
import type { EnrichedPart, PartCategory } from "../../../domain/types";
import type { DemoOutputQueue, DemoRuleStatus } from "../../../domain/ruleTypes";
import {
    runStatementRuleBoolean,
} from "../../executableFamilies/statementRule/StatementRuleIntegrationAdapter";
import { createNativeStatementRuleSummaryPresenter } from "../../executableFamilies/statementRule/nativeStatementRuleSummaryPresenter";
import { requireRulePolicyBinding } from "../rulePolicyBindingRegistry";
import type { StatementRuleIntegrationPolicy } from "../statementRulePolicyTypes";

export const DQ002_RULE_ID = "DQ-002";
export const DQ002_APPGLUE_EXECUTABLE_TYPE = "StatementRuleConfiguration";
export const DQ002_OUTPUT_QUEUE: DemoOutputQueue = "pending_review";
export const DQ002_PASSIVE_CATEGORIES = ["Capacitor", "Resistor"] as const;

export type Dq002PassiveCategory = (typeof DQ002_PASSIVE_CATEGORIES)[number];

export type Dq002UnitOfMeasureEvidence = {
    status: DemoRuleStatus;
    applicableCategory: boolean;
    passiveCategories: Dq002PassiveCategory[];
    hasUomConflict: boolean;
    appGlueRuntimeError?: string;
    outputQueue?: DemoOutputQueue;
};

const createDq002InputDefinition = (): DataDefinition => {
    const inputData = new DataDefinition();
    inputData.fields.push(new StringDataDefinition("category"));
    inputData.fields.push(new BooleanDataDefinition("has_uom_conflict"));
    return inputData;
};

export const buildDq002ExecutionData = (part: EnrichedPart): ExecutionData =>
    new ExecutionData({
        category: part.category,
        has_uom_conflict: part.hasUomConflict,
    });

const categoryEquals = (category: Dq002PassiveCategory): StringEqualExpression => {
    const expression = new StringEqualExpression();
    expression.value1.variableName = "category";
    expression.value2.value = category;
    return expression;
};

const hasUnitConflict = (): IsTrueExpression => {
    const expression = new IsTrueExpression();
    expression.value1.variableName = "has_uom_conflict";
    return expression;
};

const setBranchExpressions = (branch: IfThenPair, expressions: ExpressionRule[]) => {
    branch.ifExpression.elements = [];
    expressions.forEach((expression) => {
        branch.ifExpression.add();
        branch.ifExpression.elements[branch.ifExpression.elements.length - 1]!.subExpression = expression;
    });
};

export const createDq002StatementRuleConfiguration = (): StatementRuleConfiguration => {
    AppGlueCoreInit.init();

    const config = new StatementRuleConfiguration();
    config.title = "DQ-002 Unit-of-measure consistency";
    config.inputData = createDq002InputDefinition();
    config.executionResult = new BooleanDataDefinition();

    const expression = new IfThenExpression();
    expression.ifThenStyle = IfThenStyle.BRANCH_DATA_RETURN;

    const capacitorConflictBranch = expression.ifBranches[0]!;
    setBranchExpressions(capacitorConflictBranch, [categoryEquals("Capacitor"), hasUnitConflict()]);
    capacitorConflictBranch.thenExpression.value = false;

    const resistorConflictBranch = expression.addIfBranch();
    setBranchExpressions(resistorConflictBranch, [categoryEquals("Resistor"), hasUnitConflict()]);
    resistorConflictBranch.thenExpression.value = false;

    expression.defaultOutput.value = true;
    config.expression.expression = expression;

    return config;
};

const DQ002_SUMMARY_PRESENTER = createNativeStatementRuleSummaryPresenter({
    actionLabel: (_config, context) =>
        context?.definition?.actions.find((action) => action.id === "route-conflicts")?.label ??
        "route unresolved conflicts to review",
});

const evaluateDq002Part = (config: StatementRuleConfiguration, part: EnrichedPart) => {
    const { appGluePassed, appGlueRuntimeError } = runStatementRuleBoolean(config, buildDq002ExecutionData(part));
    const applicableCategory = (DQ002_PASSIVE_CATEGORIES as readonly PartCategory[]).includes(part.category);
    const status: DemoRuleStatus = appGlueRuntimeError
        ? "flagged"
        : !applicableCategory
          ? "skipped"
          : appGluePassed
            ? "pass"
            : "flagged";

    return {
        appGluePassed,
        evidence: {
            status,
            applicableCategory,
            passiveCategories: [...DQ002_PASSIVE_CATEGORIES],
            hasUomConflict: part.hasUomConflict,
            appGlueRuntimeError,
            outputQueue: status === "flagged" ? DQ002_OUTPUT_QUEUE : undefined,
        } satisfies Dq002UnitOfMeasureEvidence,
    };
};

export const DQ002_STATEMENT_RULE_POLICY: StatementRuleIntegrationPolicy<EnrichedPart> = {
    ruleId: DQ002_RULE_ID,
    policyBinding: requireRulePolicyBinding(DQ002_RULE_ID),
    createConfig: createDq002StatementRuleConfiguration,
    evaluator: {
        evaluate: (config, part) => {
            const { appGluePassed, evidence } = evaluateDq002Part(config, part);

            return {
                passed: appGluePassed,
                status: evidence.status,
                reasonCode: evidence.appGlueRuntimeError
                    ? "APPGLUE_EXPRESSION_INCOMPLETE"
                    : evidence.status === "skipped"
                        ? "NO_PASSIVE_UOM"
                        : evidence.status === "pass"
                          ? "UOM_NORMALIZED"
                          : "UNRESOLVED_UOM_CONFLICT",
                explanation: evidence.appGlueRuntimeError
                    ? "The AppGlue expression is incomplete, so validation preview is holding rows until the expression is completed."
                    : evidence.status === "skipped"
                        ? "Unit normalization rule applies to passive components only."
                        : evidence.status === "pass"
                          ? "Passive component units are normalized to the canonical demo unit."
                          : "A passive component has conflicting source units that require human review before publish.",
                outputQueue: evidence.outputQueue,
                comparisons: evidence.appGlueRuntimeError
                    ? [
                          {
                              label: "AppGlue expression",
                              expected: "complete executable expression",
                              actual: evidence.appGlueRuntimeError,
                              ok: false,
                          },
                      ]
                    : evidence.status === "skipped"
                        ? [
                              {
                                  label: "Category",
                                  expected: evidence.passiveCategories.join(" or "),
                                  actual: part.category,
                                  ok: false,
                              },
                          ]
                        : [
                              {
                                  label: "Unit normalization",
                                  expected: "resolved",
                                  actual: evidence.hasUomConflict ? "conflict" : "resolved",
                                  ok: !evidence.hasUomConflict,
                              },
                          ],
                runtimeEvidence: {
                    runtime: "appglue_runtime",
                    executableType: DQ002_APPGLUE_EXECUTABLE_TYPE,
                },
                evidence,
            };
        },
    },
    summaryPresenter: DQ002_SUMMARY_PRESENTER,
    runtimeSamples: {
        get passingRow() {
            return DQ002_RUNTIME_SAMPLES.passingRow;
        },
        get routedRow() {
            return DQ002_RUNTIME_SAMPLES.routedRow;
        },
    },
};

const samplePart = (overrides: Partial<EnrichedPart> = {}): EnrichedPart => ({
    partId: "PART-DQ002-SAMPLE",
    mpn: "VJ0001CAP",
    manufacturerId: "MFR-004",
    manufacturerName: "Vishay",
    category: "Capacitor",
    taxonomyPath: "Capacitor > Ceramic > MLCC",
    packageType: "0402",
    pinCount: null,
    voltageMin: 16,
    voltageMax: 16,
    capacitancePf: 100000,
    resistanceOhms: null,
    tolerancePct: 10,
    rohsStatus: "Compliant",
    reachStatus: "Compliant",
    lifecycleStatus: "Active",
    priorLifecycleStatus: "Active",
    confidenceScore: 94,
    source: "manufacturer_direct",
    recommendedReplacementMpn: null,
    hasMandatoryAttributeGap: false,
    hasUomConflict: false,
    hasMpnFormatIssue: false,
    hasComplianceChange: false,
    hasSourceConflict: false,
    ...overrides,
});

export const DQ002_RUNTIME_SAMPLES = {
    passingRow: samplePart(),
    routedRow: samplePart({
        partId: "PART-DQ002-ROUTED",
        hasUomConflict: true,
    }),
};
